import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Container, Row, Col } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Card, List, Button, Input, message } from 'antd';
import 'antd/dist/reset.css';

const { TextArea } = Input;

const ChatManagerComponent = () => {
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef(null);

  // Lấy tên nhân viên từ localStorage (ví dụ: "staff")
  const currentUser = localStorage.getItem('name') || 'staff';

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Load danh sách hội thoại (chỉ dựa vào tin khách hàng gửi - type "KH")
  const fetchConversations = useCallback(async () => {
    try {
      const res = await fetch('/api/chat/conversations');
      const data = await res.json();
      if (Array.isArray(data)) {
        setConversations(data);
      } else {
        console.error('Conversations response is not an array:', data);
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
    }
  }, []);

  // Load tin nhắn của hội thoại theo customer
  const fetchConversationMessages = useCallback(async (customer) => {
    try {
      const res = await fetch(`/api/chat/conversation/${customer}`);
      const data = await res.json();
      if (Array.isArray(data)) {
        setConversationMessages(data);
      } else {
        console.error('Conversation messages response is not an array:', data);
      }
    } catch (error) {
      console.error('Error fetching conversation messages:', error);
    }
  }, []);

  // Khi gửi tin nhắn từ ChatManagerComponent thì type là "NV"
  const handleSendMessage = async () => {
    const contentToSend = newMessage.trim();
    if (!contentToSend || !selectedConversation) return;
    const customer = selectedConversation.customer;
    try {
      const res = await fetch(`/api/chat/conversation/${customer}/send`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: contentToSend, sender: currentUser, type: 'NV' }),
      });
      if (!res.ok) throw new Error('Error sending message');
      const msg = await res.json();
      setConversationMessages(prev => [...prev, msg]);
      setNewMessage('');
    } catch (error) {
      console.error(error);
      message.error('Error sending message');
    }
  };

  /*  
    Ở giao diện nhân viên, khi xem hội thoại, gửi reader = currentUser (vì currentUser ≠ customer).
    Theo backend, với reader != customer thì sẽ cập nhật các tin có type = "KH" (tin khách hàng gửi)
    thành đã đọc.
  */
  const markConversationRead = useCallback(async (customer) => {
    try {
      const res = await fetch(`/api/chat/conversation/${customer}/mark_read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reader: currentUser }),
      });
      if (!res.ok) throw new Error('Error marking messages as read');
      const data = await res.json();
      console.log(data.message);
    } catch (error) {
      console.error('Error marking messages as read:', error);
    }
  }, [currentUser]);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(() => {
      fetchConversations();
    }, 5000);
    return () => clearInterval(interval);
  }, [fetchConversations]);

  useEffect(() => {
    if (selectedConversation) {
      const customer = selectedConversation.customer;
      markConversationRead(customer);
      fetchConversationMessages(customer);
      const interval = setInterval(() => {
        markConversationRead(customer);
        fetchConversationMessages(customer);
      }, 5000);
      return () => clearInterval(interval);
    }
  }, [selectedConversation, markConversationRead, fetchConversationMessages]);

  useEffect(() => {
    scrollToBottom();
  }, [conversationMessages]);

  const handleSelectConversation = conversation => {
    setSelectedConversation(conversation);
    fetchConversationMessages(conversation.customer);
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <Container fluid style={{ height: '80vh' }}>
      <Row noGutters>
        {/* Cột danh sách hội thoại – chỉ load tin của khách (type "KH") */}
        <Col xs={12} md={4} style={{ borderRight: '1px solid #ddd', overflowY: 'auto', backgroundColor: '#fff' }}>
          <Card
            title="Danh sách khách hàng"
            bordered={false}
            style={{ margin: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
          >
            <List
              itemLayout="horizontal"
              dataSource={conversations}
              renderItem={item => (
                <List.Item
                  onClick={() => handleSelectConversation(item)}
                  style={{
                    cursor: 'pointer',
                    padding: '10px 0',
                    borderLeft: item.has_unread ? '4px solid #dc3545' : 'none',
                  }}
                >
                  <List.Item.Meta
                    title={<span style={{ fontWeight: 'bold' }}>{item.customer}</span>}
                    description={
                      <div style={{ fontSize: '12px', color: '#888' }}>
                        {item.last_message && item.last_message.length > 50
                          ? item.last_message.substring(0, 50) + '...'
                          : item.last_message}
                        <br />
                        <small>{item.last_timestamp}</small>
                      </div>
                    }
                  />
                </List.Item>
              )}
            />
          </Card>
        </Col>
        {/* Cột chi tiết hội thoại */}
        <Col xs={12} md={8} style={{ height: '75vh', flexDirection: 'column' }}>
          {selectedConversation ? (
            <Card
              title={`Hội thoại với khách: ${selectedConversation.customer}`}
              bordered={false}
              style={{ flexGrow: 1, margin: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
            >
              <div style={{ flexGrow: 1, overflowY: 'auto', padding: '1rem', background: '#f8f9fa', height: '65vh' }}>
                {conversationMessages.map((msg, index) => {
                  const isStaffMessage = msg.sender === currentUser;
                  const messageStyle = {
                    maxWidth: '80%',
                    padding: '10px',
                    borderRadius: '10px',
                    backgroundColor: isStaffMessage ? '#1890ff' : '#e9ecef',
                    color: isStaffMessage ? '#fff' : '#333',
                    wordBreak: 'break-word',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  };
                  return (
                    <div
                      key={`${msg.id}-${index}`}
                      style={{
                        display: 'flex',
                        justifyContent: isStaffMessage ? 'flex-end' : 'flex-start',
                        marginBottom: '8px'
                      }}
                    >
                      <div style={messageStyle}>
                        <div>{msg.content}</div>
                        <div style={{ fontSize: '10px', textAlign: 'right', marginTop: '4px' }}>
                          {new Date(msg.timestamp).toLocaleTimeString()}
                        </div>
                        {msg.read_time && (
                          <div style={{ fontSize: '10px', textAlign: 'right', marginTop: '2px', color: '#FF3333' }}>
                            Đã đọc: {msg.read_time} {msg.read_user ? `- Người đọc: ${msg.read_user}` : ''}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
              <div style={{ borderTop: '1px solid #ddd', padding: '10px', marginTop: '10px', backgroundColor: '#fff', borderRadius: '8px' }}>
                <div style={{ display: 'flex' }}>
                  <TextArea
                    value={newMessage}
                    onChange={e => setNewMessage(e.target.value)}
                    placeholder="Nhập tin nhắn..."
                    autoSize={{ minRows: 1, maxRows: 4 }}
                    onKeyDown={handleKeyDown}
                    style={{ flexGrow: 1, marginRight: '10px' }}
                  />
                  <Button type="primary" onClick={handleSendMessage}>
                    Gửi
                  </Button>
                </div>
              </div>
            </Card>
          ) : (
            <Card bordered={false} style={{ margin: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}>
              <div style={{ padding: '20px', textAlign: 'center' }}>
                Chọn một cuộc hội thoại để xem chi tiết.
              </div>
            </Card>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ChatManagerComponent;
